import { useState, useCallback, useRef, useEffect } from "react";
import { getVehicleOptions } from "@/lib/api";
import type { VehicleSelectorResponse } from "@/lib/types";

type DropdownOptions = VehicleSelectorResponse["dropdowns"][string]["options"];
const EMPTY_OPTIONS: DropdownOptions = [];

interface UseVehicleSelectorOptions {
  /** Called when all three dropdowns are selected and vehicle_key is available */
  onVehicleComplete?: (vehicleKey: string) => void;
  /** Called when vehicle selection is cleared */
  onVehicleClear?: () => void;
}

export interface VehicleSelectorState {
  year: string;
  make: string;
  model: string;
  vehicleKey: string | null;
  yearOptions: DropdownOptions;
  makeOptions: DropdownOptions;
  modelOptions: DropdownOptions;
  isLoading: boolean;
  isInitialLoad: boolean;
  handleYearChange: (value: string) => void;
  handleMakeChange: (value: string) => void;
  handleModelChange: (value: string) => void;
  handleClear: () => void;
}

export function useVehicleSelector(
  options?: UseVehicleSelectorOptions
): VehicleSelectorState {
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [data, setData] = useState<VehicleSelectorResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const cacheRef = useRef<Map<string, VehicleSelectorResponse>>(new Map());

  // Keep latest callbacks in refs to avoid stale closures
  const onCompleteRef = useRef(options?.onVehicleComplete);
  const onClearRef = useRef(options?.onVehicleClear);
  useEffect(() => {
    onCompleteRef.current = options?.onVehicleComplete;
    onClearRef.current = options?.onVehicleClear;
  });

  const abortRef = useRef<AbortController | null>(null);

  const fetchOptions = useCallback(
    async (y?: string, m?: string, mo?: string) => {
      const cacheKey = `${y || ""}-${m || ""}-${mo || ""}`;
      const cached = cacheRef.current.get(cacheKey);
      if (cached) {
        setData(cached);
        setIsInitialLoad(false);
        if (cached.selection.is_complete && cached.selection.vehicle_key) {
          onCompleteRef.current?.(cached.selection.vehicle_key);
        }
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      try {
        const result = await getVehicleOptions(
          y || undefined,
          m || undefined,
          mo || undefined
        );
        if (controller.signal.aborted) return;
        cacheRef.current.set(cacheKey, result);
        setData(result);

        if (result.selection.is_complete && result.selection.vehicle_key) {
          onCompleteRef.current?.(result.selection.vehicle_key);
        }
      } catch {
        // ignore
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
    },
    []
  );

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current = controller;

    (async () => {
      const cacheKey = "--";
      if (cacheRef.current.has(cacheKey)) {
        setData(cacheRef.current.get(cacheKey)!);
        setIsInitialLoad(false);
        return;
      }
      setIsLoading(true);
      try {
        const result = await getVehicleOptions();
        if (controller.signal.aborted) return;
        cacheRef.current.set(cacheKey, result);
        setData(result);
      } catch { /* ignore */ } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setIsInitialLoad(false);
        }
      }
    })();
    return () => { controller.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const vehicleKey =
    data?.selection.is_complete && data.selection.vehicle_key
      ? data.selection.vehicle_key
      : null;

  // Use refs to read current year/make inside stable callbacks
  const yearRef = useRef(year);
  yearRef.current = year;
  const makeRef = useRef(make);
  makeRef.current = make;

  const handleYearChange = useCallback((value: string) => {
    setYear(value);
    setMake("");
    setModel("");
    fetchOptions(value);
  }, [fetchOptions]);

  const handleMakeChange = useCallback((value: string) => {
    setMake(value);
    setModel("");
    fetchOptions(yearRef.current, value);
  }, [fetchOptions]);

  const handleModelChange = useCallback((value: string) => {
    setModel(value);
    fetchOptions(yearRef.current, makeRef.current, value);
  }, [fetchOptions]);

  const handleClear = useCallback(() => {
    setYear("");
    setMake("");
    setModel("");
    setData(null);
    onClearRef.current?.();
    fetchOptions();
  }, [fetchOptions]);

  return {
    year,
    make,
    model,
    vehicleKey,
    yearOptions: data?.dropdowns?.year?.options ?? EMPTY_OPTIONS,
    makeOptions: data?.dropdowns?.make?.options ?? EMPTY_OPTIONS,
    modelOptions: data?.dropdowns?.model?.options ?? EMPTY_OPTIONS,
    isLoading,
    isInitialLoad,
    handleYearChange,
    handleMakeChange,
    handleModelChange,
    handleClear,
  };
}
